// src/hooks/useInstallPrompt.js
//
// Drives the "Install Previu" banner.
//   Android/desktop Chrome/Edge: the browser fires `beforeinstallprompt`; we stash
//     it and call .prompt() when the person taps Install.
//   iOS Safari: there is no prompt API, so we show Share -> Add to Home Screen
//     instructions instead.
// Hidden when already installed, on the sign-in/registration screens (don't
// interrupt the first task), and for 30 days after the person dismisses it.

import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { isIosSafari, isStandalone } from '../utils/platform';

const DISMISSED_KEY = 'previu:install-dismissed-at';
const SNOOZE_MS = 30 * 24 * 60 * 60 * 1000;
const QUIET_PATHS = ['/login', '/nav-login', '/subscriber-login', '/subscriber-register', '/register', '/doctor-login', '/install'];

function recentlyDismissed() {
  try {
    const at = Number(localStorage.getItem(DISMISSED_KEY));
    return at && Date.now() - at < SNOOZE_MS;
  } catch (e) {
    return false;
  }
}

export default function useInstallPrompt() {
  const { pathname } = useLocation();
  const [deferred, setDeferred] = useState(null);
  const [installed, setInstalled] = useState(false);
  const [snoozed, setSnoozed] = useState(recentlyDismissed);

  useEffect(() => {
    const onPrompt = (e) => { e.preventDefault(); setDeferred(e); };
    const onInstalled = () => { setInstalled(true); setDeferred(null); };
    window.addEventListener('beforeinstallprompt', onPrompt);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onPrompt);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const dismiss = useCallback(() => {
    try { localStorage.setItem(DISMISSED_KEY, String(Date.now())); } catch (e) { /* private mode */ }
    setSnoozed(true);
  }, []);

  const install = useCallback(async () => {
    if (!deferred) return;
    deferred.prompt();
    await deferred.userChoice;
    setDeferred(null);
  }, [deferred]);

  const quiet = QUIET_PATHS.some((p) => pathname.startsWith(p)) || pathname === '/';
  const blocked = installed || snoozed || quiet || isStandalone();
  const mode = blocked ? null : deferred ? 'prompt' : isIosSafari() ? 'ios' : null;

  return { mode, install, dismiss };
}
